// Unified-portal "Pitstop Notifications" tab, hosted inside admin-ui.
//
// Ported from pitstop-ui's notification-setting.jsx, adapted to admin-ui:
//   - talks to pitstop-core cross-backend via pitstopSettingsApi (fetch + pitstop headers)
//   - ToggleSwitch2 (same {item,labelText,callback} API as pitstop's ToggleSwitch)
//   - useToast instead of pitstop's ToastContext
//   - a stale pitstop token (401) shows a tab-level message instead of logging the
//     user out of the admin surface.
//
// The EMAIL_PREFERENCE "Data Element" modify block sources its data-element universe
// from pitstop config (produces/consumes). admin-ui doesn't load that at login, so we
// reuse pitstop-ui's sessionStorage `config` cache when present, else fetch it
// cross-backend — only when the feature is on. (A3-3 adds proactive token renewal.)
import { ToggleSwitch } from "@/components/ToggleSwitch2/index";
import { Fragment, useEffect, useState } from "react";
import { ResetNotificationModal } from "../modals/reset-notification-modal.jsx";
import { ReactComponent as IconRefresh } from "@/assets/svgs/refresh.svg";
import useModal from "@/hooks/use-modal/index";
import useToast from "@/hooks/use-toast";
import { useTradex } from "@/hooks/use-tradex/index";
import { useFeature } from "@/hooks/use-feature/index.js";
import { featureConstant } from "@/constants/configuration/feature.js";
import Button from "@/componentsDS/Button/Button";
import { ButtonSize, ButtonType, ButtonVariant } from "@/componentsDS/Button/Button.type.js";
import Modal from "@/dsComponents/Modal";
import { ModalBody, ModalFooter, ModalFooterLeft, ModalFooterRight, ModalHeader, ModalTitle } from "@dextech-ai/dex-design-system";
import { NotificationsOutlined } from "@dextech-ai/dex-design-system/icons";
import InputDropdown from "@/dsComponents/select/InputDropdownSelect";
import { pitstopSettingsApi } from "@/services/api/pitstop-settings.js";

function computeDataElements(cfg) {
	const combined = [...(cfg?.consumes || []), ...(cfg?.produces || [])];
	const unique = combined.reduce(
		(acc, item) => (acc.some((el) => el.id === item.id) ? acc : [...acc, item]),
		[]
	);
	return unique.map((item) => ({ ...item, id: item.id, label: item.name, uncategorized: "uncategorized" }));
}

// A corrupt/stale cache entry must degrade to the cross-backend fetch, never throw.
function safeParseJson(str) {
	try {
		return JSON.parse(str);
	} catch {
		return null;
	}
}

// Deep-clone the grouped settings before an optimistic mutation (plain data only).
const cloneState = (state) => structuredClone(state);

export const PitstopNotificationSetting = ({ setLoading }) => {
	const dailyReportKeyWord = "Daily Reports";
	const { addToastMessage } = useToast();
	const isTradex = useTradex();
	const isEmailPreferenceEnabled = useFeature(featureConstant.EMAIL_PREFERENCE);

	const [notificationSettings, setNotificationSettings] = useState([]);
	const [transformedDataElements, setTransformedDataElements] = useState([]);
	const [selectedDataElements, setSelectedDataElements] = useState([]);
	const [dropdownSearch, setDropdownSearch] = useState("");
	const [sessionExpired, setSessionExpired] = useState(false);

	const { visible: isModalVisible, show: showModal, hide: hideModal } = useModal();
	const {
		visible: visibleDataElementNotification,
		show: showDataElementNotification,
		hide: hideDataElementNotification,
	} = useModal();

	// Data-element universe (only when EMAIL_PREFERENCE is on): prefer pitstop-ui's
	// own session cache, else fetch cross-backend. No cache is written from admin.
	async function loadDataElementUniverse() {
		if (!isEmailPreferenceEnabled) return [];
		const cachedConfig = safeParseJson(sessionStorage.getItem("config"));
		if (cachedConfig) return computeDataElements(cachedConfig);
		const [err, data] = await pitstopSettingsApi.getConfig();
		if (err || !data) return [];
		return computeDataElements(data);
	}

	function formatter(response) {
		try {
			response.sort((a, b) => (a.sequenceNo > b.sequenceNo ? 1 : -1));
			return response.reduce((prev, curr) => {
				if (!prev.some((g) => g.description === curr.group)) {
					prev.push({ description: curr.group, checked: curr.notificationEnabled, subgroups: [] });
				}
				const group = prev.find((g) => g.description === curr.group);
				group.checked = curr.notificationEnabled || group.checked;

				if (!group.subgroups.some((sg) => sg.description === curr.subGroup)) {
					group.subgroups.push({ description: curr.subGroup, items: [] });
				}
				const subGroup = group.subgroups.find((sg) => sg.description === curr.subGroup);
				subGroup.items.push({
					id: curr.id,
					description: curr.description,
					checked: curr.notificationEnabled,
					sequenceNo: curr.sequenceNo,
				});
				return prev;
			}, []);
		} catch {
			addToastMessage("Failed to format notification response", "error");
			return [];
		}
	}

	async function getNotifications(deUniverse = transformedDataElements, mounted = true) {
		let [error, response] = await pitstopSettingsApi.getNotifications({ setLoading, addToastMessage });

		if (!mounted) return;
		if (error) {
			if (error.sessionExpired) setSessionExpired(true);
			setNotificationSettings([]);
			return;
		}

		if (!isTradex) {
			response = response.filter(
				(item) =>
					item.subGroup !== "Package" &&
					item.subGroup !== "Record Transfer" &&
					item.subGroup !== "Maker Checker"
			);
		}

		if (!response.length) return;
		setNotificationSettings(formatter(response));

		if (isEmailPreferenceEnabled) {
			const enabled = response[0]?.dataElementIds || [];
			setSelectedDataElements(deUniverse.filter((el) => enabled.includes(el.id)));
		}
	}

	// Off-tenants keep A3-1's safe behavior (no dataElementIds); on-tenants carry the
	// current selection so a toggle-save preserves it.
	function withDataElements(base) {
		if (!isEmailPreferenceEnabled) return base;
		return { ...base, dataElementIds: selectedDataElements.map((el) => el.id) };
	}

	async function updateNotifications(requestPayload, newState, mounted = true) {
		const [error] = await pitstopSettingsApi.updateNotifications(requestPayload, { setLoading, addToastMessage });
		if (!mounted) return;
		if (error) {
			if (error.sessionExpired) setSessionExpired(true);
			return;
		}
		setNotificationSettings(newState);
	}

	useEffect(() => {
		let mounted = true;
		(async () => {
			// Proactively refresh the pitstop token first (it ages while admin is the
			// active surface). A 401 here = already expired → show the message and skip
			// the fetches (they'd 401 too). Any other renew failure is non-fatal: fall
			// through and use the existing token.
			const [renewErr] = await pitstopSettingsApi.renewToken();
			if (!mounted) return;
			if (renewErr?.sessionExpired) {
				setSessionExpired(true);
				return;
			}
			const de = await loadDataElementUniverse();
			if (!mounted) return;
			setTransformedDataElements(de);
			await getNotifications(de, mounted);
		})();
		return () => { mounted = false; };
	}, []);

	async function onSingleNotificationChanged(groupIdx, subgroupIdx, itemIdx) {
		const newState = cloneState(notificationSettings);
		const item = newState[groupIdx].subgroups[subgroupIdx].items[itemIdx];
		item.checked = !item.checked;

		newState[groupIdx].checked = newState[groupIdx].subgroups.reduce(
			(prev, curr) => prev || curr.items.some((itm) => itm.checked),
			false
		);

		const requestPayload = [
			withDataElements({ emailNotificationId: item.id, userNotificationEnabled: item.checked }),
		];
		await updateNotifications(requestPayload, newState);
	}

	function getChangedNotifications(newState, groupIdx, targetValue) {
		const group = newState[groupIdx];
		group.checked = targetValue;

		// Plain loops (not nested callbacks): mutate the cloned state in place and
		// collect one payload row per item whose value actually changes.
		const requestPayload = [];
		for (const subgroup of group.subgroups) {
			for (const item of subgroup.items) {
				if (item.checked === targetValue) continue;
				item.checked = targetValue;
				requestPayload.push(
					withDataElements({ emailNotificationId: item.id, userNotificationEnabled: targetValue })
				);
			}
		}
		return requestPayload;
	}

	async function onMultiNotificationChanged(groupIdx) {
		const newState = cloneState(notificationSettings);
		const requestPayload = getChangedNotifications(newState, groupIdx, !newState[groupIdx].checked);
		await updateNotifications(requestPayload, newState);
	}

	async function onResetModalConfirm() {
		const newState = cloneState(notificationSettings);
		const requestPayload = newState.reduce(
			(prev, curr, currIdx, callerObj) => [...prev, ...getChangedNotifications(callerObj, currIdx, true)],
			[]
		);
		await updateNotifications(requestPayload, newState);
	}

	// Data-element modal: save the selected data elements against every notification row.
	function updateDERequestPayload() {
		const dataElementIds = selectedDataElements.map((el) => el.id);
		return notificationSettings.flatMap((group) =>
			group.subgroups.flatMap((subgroup) =>
				subgroup.items.map((item) => ({
					emailNotificationId: item.id,
					userNotificationEnabled: item.checked,
					dataElementIds,
				}))
			)
		);
	}

	const handleConfirm = async () => {
		await updateNotifications(updateDERequestPayload(), notificationSettings, true);
		await getNotifications(transformedDataElements);
		hideDataElementNotification();
	};

	const dropdownSearchHandler = (e) => {
		if (e?.target) setDropdownSearch(e.target.value);
	};

	// One subgroup block (title + item rows). A named render helper keeps the JSX
	// callback nesting within limits and gives rows stable keys (item.id).
	const renderSubgroup = (group, groupIdx, subgroup, subgroupIdx) => (
		<div key={`${group.description}-${subgroup.description ?? "general"}`}>
			{subgroup.description && (
				<div className="subgroup-title font-weight-bold mt-1 px-1">
					{subgroup.description}
				</div>
			)}
			{subgroup.items?.map((item, itemIdx) => (
				<div
					key={item.id}
					className={`notification-item-container flex-box p-1 ${
						subgroup.description ? "" : " notification-item-container-no-hover"
					}`}
				>
					<div className="notification-item font-weight-normal flex-10">
						{item.description}
					</div>
					<div className="flex-2">
						{subgroup.description ? (
							<ToggleSwitch
								data-testid="item-toggle"
								item={item}
								labelText={item.checked ? "On" : "Off"}
								callback={() =>
									onSingleNotificationChanged(groupIdx, subgroupIdx, itemIdx)
								}
							/>
						) : (
							<div></div>
						)}
					</div>
				</div>
			))}
		</div>
	);

	if (sessionExpired) {
		return (
			<div className="notification-container">
				<div className="notification-note">
					<span className="font-weight-normal">
						Your pitstop session has expired. Please log in again to manage pitstop notifications.
					</span>
				</div>
			</div>
		);
	}

	return (
		<>
			<ResetNotificationModal
				isModalVisible={isModalVisible}
				hideModal={hideModal}
				confirmCallback={onResetModalConfirm}
			/>
			{/* Full-width anchor row: reset control lines up with the 3-tab bar
			    without overlapping it. */}
			<div className="account-settings__reset-row">
				<div
					data-testid="reset-default-notification-btn"
					className="reset-default-notification-btn"
					role="button"
					tabIndex={0}
					onClick={() => showModal()}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							showModal();
						}
					}}
				>
					<div className="flex-box flex-align-center">
						<div className="DS__icon">
							<IconRefresh />
						</div>
						<div className="reset-default-notification-btn-text font-weight-bold">
							Reset to default
						</div>
					</div>
				</div>
			</div>
			<div className="notification-container">
				<div className="notification-note">
					<span className="font-weight-bold text-color-purple">Note: </span>
					<span className="font-weight-normal">All changes are automatically saved.</span>
				</div>

				{notificationSettings?.map((group, groupIdx) => (
					<Fragment key={group.description}>
						<div data-testid="group-container" className="group-container">
							<div className="flex-box mt-1 px-1">
								<div className="group-title font-weight-bold flex-10">
									{group.description}
								</div>
								<div className="flex-2">
									<ToggleSwitch
										data-testid="group-toggle"
										item={group}
										labelText={group.checked ? "On" : "Off"}
										callback={() => onMultiNotificationChanged(groupIdx)}
									/>
								</div>
							</div>
							{group.description === dailyReportKeyWord && (
								<div
									data-testid="extra-description"
									className="font-size-s font-color-grey-80 mt-1 px-1 font-weight-normal"
								>
									Daily, 05:00 PM, Singapore &#40;SGT&#41;
								</div>
							)}

							<div className="group-content">
								{group.description === "Email Notifications" && isEmailPreferenceEnabled && (
									<div className="flex-box mt-1 px-1">
										<div className="flex-10">
											<div className="subgroup-title font-weight-bold">
												<span>Data Element</span>
											</div>
											<div>
												<div className="font-color-grey-80 font-size-s line-height-2">
													{selectedDataElements.length > 0
														? `Receiving notifications for ${selectedDataElements.length} data element(s)`
														: "You are not receiving notifications for any data elements"}
												</div>
											</div>
										</div>
										<div className="flex-2">
											<div className="flex-box flex-end DS__button--interactive-disabled">
												<Button
													text="Modify"
													type={ButtonType.GHOST}
													variant={ButtonVariant.STANDARD}
													size={ButtonSize.SMALL}
													onClick={() => showDataElementNotification()}
												/>
											</div>
										</div>
									</div>
								)}
								{group.subgroups?.map((subgroup, subgroupIdx) =>
									renderSubgroup(group, groupIdx, subgroup, subgroupIdx)
								)}
							</div>
						</div>
						{groupIdx !== notificationSettings.length - 1 && <hr className="separation-line" />}
					</Fragment>
				))}
			</div>

			<Modal
				dataTestId="data-element-modal"
				addClass="DS__button--interactive-disabled"
				visible={visibleDataElementNotification}
				close={hideDataElementNotification}
				dismissClickOutsideModal={false}
			>
				<ModalHeader icon={<NotificationsOutlined />}>
					<ModalTitle>Email Notifications - Data Element</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<div className="notification-modal">
						<div className="notification-modal__top">
							Receive notification for the following Data Elements.
						</div>
						<div className="notification-modal__bottom">
							<InputDropdown
								value={dropdownSearch}
								onChange={dropdownSearchHandler}
								name={"dataElementSearch"}
								dropdownOption={transformedDataElements}
								placeholder={"Select an option..."}
								onSelectDropdownItem={setSelectedDataElements}
								dropdownSelectedOption={selectedDataElements}
								multipleSelection={true}
							/>
						</div>
					</div>
				</ModalBody>
				<ModalFooter>
					<ModalFooterLeft>
						<Button
							text="Cancel"
							type={ButtonType.GHOST}
							variant={ButtonVariant.STANDARD}
							size={ButtonSize.SMALL}
							onClick={hideDataElementNotification}
						/>
					</ModalFooterLeft>
					<ModalFooterRight>
						<Button
							text="Confirm"
							type={ButtonType.SOLID}
							variant={ButtonVariant.STANDARD}
							size={ButtonSize.SMALL}
							onClick={handleConfirm}
						/>
					</ModalFooterRight>
				</ModalFooter>
			</Modal>
		</>
	);
};
